import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDevices, addDevice, updateDevice, deleteDevice, testConnection, manualSync, clearMessages } from '../../redux/biometricSlice';
import { Settings, Plus, RefreshCw, Trash2, Edit2, CheckCircle, XCircle } from 'lucide-react';
import Alert from '../../components/ui/Alert';
import Loader from '../../components/ui/Loader';
import { useToast } from '../../contexts/ToastContext';

const DeviceManagement = () => {
    const dispatch = useDispatch();
    const { devices, loading, error, successMessage, connectionTestLoading, connectionTestMessage, connectionTestError } = useSelector((state) => state.biometric);
    const { user } = useSelector((state) => state.auth);
    const { success, error: toastError } = useToast();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingDevice, setEditingDevice] = useState(null);
    const [formData, setFormData] = useState({
        deviceName: '',
        ipAddress: '',
        username: '',
        password: ''
    });

    useEffect(() => {
        dispatch(fetchDevices(user?.token));
    }, [dispatch, user?.token]);

    useEffect(() => {
        if (successMessage) {
            success(successMessage);
            dispatch(clearMessages());
            setIsModalOpen(false);
        }
        if (error) {
            toastError(error);
            dispatch(clearMessages());
        }
        if (connectionTestMessage) {
            success(connectionTestMessage);
            dispatch(clearMessages());
        }
        if (connectionTestError) {
            toastError(connectionTestError);
            dispatch(clearMessages());
        }
    }, [successMessage, error, connectionTestMessage, connectionTestError, dispatch, success, toastError]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingDevice) {
            dispatch(updateDevice({ id: editingDevice._id, deviceData: formData, token: user?.token }));
        } else {
            dispatch(addDevice({ deviceData: formData, token: user?.token }));
        }
    };

    const handleEdit = (device) => {
        setEditingDevice(device);
        setFormData({
            deviceName: device.deviceName,
            ipAddress: device.ipAddress,
            username: device.username,
            password: '' // Don't show password for security, only update if changed
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this device?')) {
            dispatch(deleteDevice({ id, token: user?.token }));
        }
    };

    const handleTestConnection = (device) => {
        dispatch(testConnection({ deviceData: device, token: user?.token }));
    };

    const handleManualSync = () => {
        dispatch(manualSync(user?.token));
    };

    const openModal = () => {
        setEditingDevice(null);
        setFormData({ deviceName: '', ipAddress: '', username: '', password: '' });
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-8 animate-fadeIn max-w-[1600px] mx-auto p-4 md:p-8">
            <div className="glass-card rounded-[2.5rem] p-8 flex flex-col md:flex-row justify-between items-center gap-6 premium-shadow">
                <div className="text-center md:text-left">
                    <h1 className="text-4xl font-black tracking-tight bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                        Biometric Devices
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium flex items-center justify-center md:justify-start gap-2">
                        <Settings size={18} className="text-[#1B6CA8]" />
                        Manage Hikvision Integration
                    </p>
                </div>
                <div className="flex gap-4">
                    <button onClick={handleManualSync} disabled={loading} className="px-6 py-3 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-xl transition-all font-bold flex items-center gap-2 shadow-sm">
                        <RefreshCw size={20} className={loading ? 'animate-spin' : ''} /> Sync Now
                    </button>
                    <button onClick={openModal} className="px-6 py-3 bg-gradient-to-r from-[#1B6CA8] to-[#12507D] hover:from-[#155A8F] hover:to-[#0F426A] text-white rounded-xl transition-all shadow-md font-bold flex items-center gap-2 premium-shadow">
                        <Plus size={20} /> Add Device
                    </button>
                </div>
            </div>

            {loading && !isModalOpen ? <Loader /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {devices.map(device => (
                        <div key={device._id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-xl font-bold text-slate-800">{device.deviceName}</h3>
                                    <p className="text-sm text-slate-500 font-mono mt-1">{device.ipAddress}</p>
                                </div>
                                <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${device.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {device.status === 'active' ? <CheckCircle size={14}/> : <XCircle size={14}/>}
                                    {device.status.toUpperCase()}
                                </div>
                            </div>
                            
                            <div className="space-y-2 mb-6">
                                <p className="text-sm text-slate-600"><span className="font-semibold">User:</span> {device.username}</p>
                                <p className="text-sm text-slate-600"><span className="font-semibold">Last Sync:</span> {device.lastSyncAt ? new Date(device.lastSyncAt).toLocaleString() : 'Never'}</p>
                            </div>

                            <div className="flex justify-between items-center border-t border-slate-100 pt-4">
                                <button 
                                    onClick={() => handleTestConnection(device)}
                                    disabled={connectionTestLoading}
                                    className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                    <RefreshCw size={16} className={connectionTestLoading ? 'animate-spin' : ''} /> Test Connection
                                </button>
                                <div className="flex gap-2">
                                    <button onClick={() => handleEdit(device)} className="p-2 text-slate-400 hover:text-blue-600 transition-colors bg-slate-50 rounded-lg"><Edit2 size={18} /></button>
                                    <button onClick={() => handleDelete(device._id)} className="p-2 text-slate-400 hover:text-red-600 transition-colors bg-slate-50 rounded-lg"><Trash2 size={18} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {devices.length === 0 && (
                        <div className="col-span-full py-12 text-center text-slate-500 bg-white rounded-2xl border border-slate-100">
                            No devices configured. Click "Add Device" to get started.
                        </div>
                    )}
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slideInUp shadow-2xl m-4">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingDevice ? 'Edit Device' : 'Add New Device'}</h2>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><XCircle size={24} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Device Name</label>
                                <input type="text" name="deviceName" value={formData.deviceName} onChange={handleInputChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">IP Address (e.g. 192.168.1.100)</label>
                                <input type="text" name="ipAddress" value={formData.ipAddress} onChange={handleInputChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Username</label>
                                <input type="text" name="username" value={formData.username} onChange={handleInputChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Password {editingDevice && '(leave blank to keep current)'}</label>
                                <input type="password" name="password" value={formData.password} onChange={handleInputChange} className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500" required={!editingDevice} />
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl font-medium transition-colors">Cancel</button>
                                <button type="submit" disabled={loading} className="px-5 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-xl font-bold transition-colors shadow-sm disabled:opacity-50">
                                    {loading ? 'Saving...' : 'Save Device'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeviceManagement;
